import {Button, showNotification} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {useToRevisionInspectionMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {updateAuditState} from '../../../store/carAudit/reducer';
import {selectAudit} from '../../../store/carAudit/selectors';
import {LoadAuditDataResponseItemBody} from '../../../types/LoadAuditDataResponseItemBody';
import {useConditionContext} from '../hooks/useConditionContext';

interface ToRevisionInspectionProps extends TestIdProps {
  onSuccess?: () => void;
}

export function ToRevisionInspection(props: ToRevisionInspectionProps) {
  const dispatch = useDispatch();

  const {loadActualDataAndValidate} = useConditionContext();

  const audit = useSelector(selectAudit);

  // Covers the whole flow, unlike the mutation's own flag: validation already does a GET and a PUT
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [toRevisionInspection] = useToRevisionInspectionMutation();

  const handleSuccess = () => {
    dispatch(
      updateAuditState({
        state: LoadAuditDataResponseItemBody.state.TO_REVISION,
      })
    );
    props.onSuccess?.();
  };

  const handleToRevision = async () => {
    if (!audit || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      try {
        await loadActualDataAndValidate();
      } catch (error) {
        showNotification.warning(
          match(error)
            .with({categoriesWithError: Pattern.any}, () =>
              i18n.t('entity.mandatoryFields.labels.missingFieldsCount')
            )
            .otherwise(() => i18n.t('entity.condition.labels.updateConflicts'))
        );

        return;
      }

      await toRevisionInspection({inspectionId: audit.id})
        .unwrap()
        .then(handleSuccess)
        .catch(handleApiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Button
      variant="secondary"
      onClick={handleToRevision}
      isLoading={isSubmitting}
      isDisabled={isSubmitting}
      title={i18n.t('general.actions.toRevision')}
      data-testid={suffixTestId('toRevisionInspection', props)}
    />
  );
}
